import React, { useEffect, useMemo, useState } from 'react';
import { Bot, History, Loader2, X } from 'lucide-react';
import { changeWhen } from './changes';

// Historique tracé de la roadmap et du budget : ce qui a changé, pourquoi, par qui, quand. Rempli seulement quand un
// rôle tient le domaine sur le projet (sinon on modifie librement, sans trace), à partir de l'état de départ du projet
// (fin du kickoff, passage « En cours »).

const FILTERS = [['all', 'Tout'], ['roadmap', 'Roadmap'], ['budget', 'Budget']];
const BADGE = { roadmap: 'bg-indigo-50 text-indigo-700', budget: 'bg-amber-50 text-amber-700', baseline: 'bg-emerald-50 text-emerald-700' };
const BADGE_LABEL = { roadmap: 'Roadmap', budget: 'Budget', baseline: 'Départ' };

export const ChangeLine = ({ change, compact = false }) => (
  <li className={compact ? 'py-1.5' : 'py-2.5'}>
    <div className="flex items-start gap-2">
      <span className={`mt-0.5 shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold ${BADGE[change.domain] || 'bg-slate-100 text-slate-600'}`}>
        {BADGE_LABEL[change.domain] || 'Roadmap'}
      </span>
      <div className="min-w-0 flex-1">
        <p className={`${compact ? 'text-xs' : 'text-[13px]'} leading-snug text-slate-800`}>{change.summary}</p>
        {change.reason && <p className={`mt-0.5 ${compact ? 'text-[11px]' : 'text-xs'} italic leading-snug text-slate-600`}>« {change.reason} »</p>}
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
          {change.source === 'nexus' && <Bot size={11} title="Par Nexus" />}
          {change.employee_name || 'Quelqu’un'}{change.source === 'nexus' ? ' (via Nexus)' : ''} · {changeWhen(change.created_at)}
        </p>
      </div>
    </div>
  </li>
);

const ChangesHistory = ({ changes, onLoad, onClose }) => {
  const [filter, setFilter] = useState('all');
  useEffect(() => { onLoad(); }, [onLoad]);
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const shown = useMemo(() => (changes || []).filter((c) => filter === 'all' || c.domain === filter || c.domain === 'baseline'), [changes, filter]);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-slate-900/20" />
      <aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 sm:px-5">
          <History size={16} className="text-slate-400" />
          <h2 className="flex-1 text-sm font-semibold text-slate-900">Historique de la roadmap et du budget</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center gap-1 px-4 pt-3 sm:px-5">
          {FILTERS.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setFilter(key)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold ${filter === key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:px-5">
          {changes === null ? (
            <p className="flex items-center gap-2 py-6 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
          ) : shown.length ? (
            <ul className="divide-y divide-slate-100">
              {shown.map((change) => <ChangeLine key={change.id} change={change} />)}
            </ul>
          ) : (
            <p className="py-6 text-sm text-slate-400">
              Aucun changement tracé. L’historique se remplit dès qu’un rôle tient la roadmap ou le budget du projet :
              chaque changement demande alors un motif.
            </p>
          )}
        </div>
      </aside>
    </div>
  );
};

export default ChangesHistory;
